import {
  type ChangeEvent,
  useState,
  useEffect,
  forwardRef
} from 'react';

import {
  DropdownChevronIcon,
  DropdownCheckMarkIcon,
} from '../../assets/Icons';
import {
  EG,
  AE,
  OM,
  PK,
  SA,
} from '../../assets/Icons/countries';
import { ALL_COUNTRIES_INFO } from '../../constants/all-countries-info';
import type { ICountries, ICountry, IPhoneInputProps } from '../../types/phone_input';
import { Label } from '../Label';
import { Input } from '../Input';
import TEXTS from "./text.json";
import styles from './index.module.scss';

export const Countries: ICountries = {
  eg: {
    name: 'Egypt',
    name_ar: 'مصر',
    countryIso2: 'eg',
    countryIso3: 'EGY',
    countryCode: '+2',
    phoneLength: 11,
    icon: EG,
  },
  pk: {
    name: 'Pakistan',
    name_ar: 'باکستان',
    countryIso2: 'pk',
    countryIso3: 'PAK',
    countryCode: '+92',
    phoneLength: 10,
    icon: PK,
  },
  ae: {
    name: 'United Arab Emirates',
    name_ar: 'الإمارات العربية المتحدة',
    countryIso2: 'ae',
    countryIso3: 'ARE',
    countryCode: '+971',
    phoneLength: 9,
    icon: AE,
  },
  sa: {
    name: 'Saudi Arabia',
    name_ar: 'المملكة العربية السعودية',
    countryIso2: 'sa',
    countryIso3: 'SAU',
    countryCode: '+966',
    phoneLength: 9,
    icon: SA,
  },
  om: {
    name: 'Oman',
    name_ar: 'عمان',
    countryIso2: 'om',
    countryIso3: 'OMN',
    countryCode: '+968',
    phoneLength: 8,
    icon: OM,
  },
};

export const PhoneInput = forwardRef<HTMLInputElement, IPhoneInputProps>(
  (props, ref) => {
    const {
      lang = 'en',
      id,
      name,
      label,
      labelHint,
      phoneNumber,
      country,
      placeholder,
      disabled,
      autoFocus = false,
      error,
      onChangeHandler,
      onCountryChange,
      disableSelectCountry,
      onFocus,
      onBlur,
      canToggleToTextInput = false,
      autoComplete = false,
      showAllCountries = false
    } = props;
    const isPhoneControlled = phoneNumber !== undefined;
    const isCountryControlled = country !== undefined;

    const countriesList = showAllCountries ? ALL_COUNTRIES_INFO : Countries;
    const countryIso2 = countriesList[country || 'eg'];

    const [internalCountry, setInternalCountry] = useState(countryIso2);
    const [internalValue, setInternalValue] = useState(phoneNumber || '');
    const [showDropdown, setShowDropdown] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');

    const selectedCountry = isCountryControlled ? (countryIso2) : internalCountry;
    const value = (isPhoneControlled ? phoneNumber : internalValue).replace(selectedCountry.countryCode, '');

    useEffect(() => {
      showDropdown === false && countrySearch &&
        setCountrySearch('');
    }, [showDropdown]);

    const toggleDropdown = () => {
      setShowDropdown((prevState) => !prevState);
    };

    const selectCountry = (country: ICountry) => {
      if (!isCountryControlled) setInternalCountry(country);
      setShowDropdown(false);
      onChangeHandler?.((country.countryCode + value), country.countryIso2, value);
      onCountryChange && onCountryChange(country.countryIso2);
    };

    const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue === '') {
        if (!isPhoneControlled) setInternalValue('');
        onChangeHandler?.('', selectedCountry.countryIso2, '');
        return;
      }

      if (!/^[0-9]+$/.test(newValue) && !canToggleToTextInput) return;

      if (!isPhoneControlled) setInternalValue(newValue);
      onChangeHandler?.(selectedCountry.countryCode + newValue, selectedCountry.countryIso2, newValue);
    };

    const handleCountrySearch = (searchTerm: string) => {
      setCountrySearch(searchTerm.trim().toLowerCase());
    };

    const filteredCountries = Object.values(countriesList).filter((country) =>
      [
        country.name,
        country.name_ar,
        country.countryIso2,
        country.countryIso3,
        country.countryCode
      ].some((field) => field.toLowerCase().includes(countrySearch))
    );

    const LOCALIZED_TEXTS = TEXTS[lang];


    return (
      <div className={styles['container']}>
        {(label || labelHint) && <Label text={label} hintText={labelHint} />}
        <div
          data-phone-input-invalid={!!error}
          className={styles['container__controller']}
          data-phone-input-disabled={disabled}
          tabIndex={0}
        >
          <button
            type="button"
            className={styles['container__controller__button']}
            onClick={toggleDropdown}
            disabled={disabled || disableSelectCountry}
            data-phone-input-btn-toggle={showDropdown}
          >
            <div
              className={styles['container__controller__button__country-icon']}
            >
              {selectedCountry?.icon}
            </div>
            <label>{selectedCountry?.countryCode}</label>
            <div
              className={styles['container__controller__button__chevron-icon']}
            >
              {!disableSelectCountry && DropdownChevronIcon}
            </div>
          </button>
          <input
            id={id}
            name={name}
            onChange={handlePhoneChange}
            value={value}
            className={styles['container__controller__input']}
            placeholder={placeholder}
            maxLength={countriesList[selectedCountry.countryIso2].phoneLength}
            autoComplete={autoComplete ? 'on' : 'one-time-code'}
            type="tel"
            inputMode="tel"
            disabled={disabled}
            autoFocus={autoFocus}
            onFocus={onFocus}
            onBlur={onBlur}
            ref={ref}
          />
        </div>
        {
          showDropdown &&
          <div
            className={styles['container__dropdown']}
            data-show-all-countries={showAllCountries}
            data-no-countries-found={!filteredCountries.length}
          >
            {
              showAllCountries &&

              <div className={styles['container__dropdown__countries-search-bar']}>
                <Input
                  name={`${name}-search-bar`}
                  placeholder={LOCALIZED_TEXTS.searchForCountry}
                  onChange={(e) => handleCountrySearch(e.target.value)}
                  autoFocus
                />
              </div>
            }
            {
              filteredCountries.map((country) => (
                <div
                  key={country.countryIso2}
                  className={styles['container__dropdown__country']}
                  data-phone-input-country-active={
                    selectedCountry.countryCode === country.countryCode
                  }
                  onClick={() => selectCountry(country)}
                >
                  <div
                    className={styles['container__dropdown__country__details']}
                  >
                    <div
                      className={
                        styles['container__dropdown__country__details__icon']
                      }
                    >
                      {country.icon}
                    </div>
                    <div
                      className={
                        styles['container__dropdown__country__details__info']
                      }
                    >
                      <div>{country[lang === 'en' ? 'name' : 'name_ar']}</div>
                      <div>{country.countryCode}</div>
                    </div>
                  </div>
                  {
                    selectedCountry.countryCode === country.countryCode &&
                    <div
                      className={styles['container__dropdown__country__trailing-icon']}
                    >
                      {DropdownCheckMarkIcon}
                    </div>
                  }
                </div>
              ))
            }
            {
              !filteredCountries.length &&
              <div className={styles['container__dropdown__no-results']}>
                {LOCALIZED_TEXTS.noResults}
              </div>
            }
          </div>
        }
        {error && <span className={styles['container__error']}>{error}</span>}
      </div>
    );
  }
);
